/* eslint-disable react-refresh/only-export-components --
 * Provider and consumer hook are intrinsically paired, same shape as
 * @/auth/access-features.
 */
import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@apollo/client/react";
import { apolloClient } from "@/graphql/client";
import { MyPermissionsDocument } from "@/graphql/generated/operations";

// Genesis Permission codes a gate may check via useHasPermission. Grows one
// entry per gate migrated off a retired Keycloak role, mirroring genesis's
// own Permission enum codes rather than restating them loosely as strings at
// each call site.
export const Permission = {
  ReadOrganizationalRecord: "read:organizational-record",
  WriteOrganizationalRecord: "write:organizational-record",
  ProvisionInvitee: "provision:invitee",
  WriteAccessConfig: "write:access-config",
  WriteImport: "write:import",
  WriteQuestionnaire: "write:questionnaire",
  WriteReferenceData: "write:reference-data",
  CreateCompany: "create:company",
  WritePerson: "write:person",
} as const;

// Holding any of these grants access to the Systemeinstellungen surface -
// the Permission-based replacement for roles.ts's retired SYSTEM_ROLES.
const SYSTEM_PERMISSIONS: readonly string[] = [
  Permission.WriteImport,
  Permission.WriteAccessConfig,
  Permission.WriteQuestionnaire,
  Permission.WriteReferenceData,
];

// Genesis 242-access-model: which Permission codes (`provision:invitee`,
// `write:access-config`, …) the current principal holds, read once per
// session via `myPermissions` - the rights projection's counterpart to
// ReachableFeatures on the sight side. Genesis is the authority; this only
// reflects what it already decided, never a Keycloak role read client-side.
//
// Fail-closed by construction, same rule as access-features: while loading or
// on any error, `data` is undefined, so `permissionCodes` resolves to an
// empty array and `hasPermission` answers false for everything.
interface ContextValue {
  hasPermission: (code: string) => boolean;
  loading: boolean;
}

const AccessPermissionsContext = createContext<ContextValue>({
  hasPermission: () => false,
  loading: false,
});

export function AccessPermissionsProvider({ children }: { children: ReactNode }) {
  const { data, loading } = useQuery(MyPermissionsDocument, {
    fetchPolicy: "network-only",
  });
  const permissionCodes = data?.myPermissions.permissionCodes ?? [];
  const value: ContextValue = {
    hasPermission: (code) => permissionCodes.includes(code),
    loading,
  };

  return (
    <AccessPermissionsContext.Provider value={value}>
      {children}
    </AccessPermissionsContext.Provider>
  );
}

export function useHasPermission(code: string): boolean {
  return useContext(AccessPermissionsContext).hasPermission(code);
}

// For call sites that need to check more than one code without calling a
// hook per code inside a loop (React's rules of hooks forbid that) - e.g.
// SettingsPage's per-section Permission map, same shape as
// @/auth/access-features's useAccessFeatures.
export function useAccessPermissions(): ContextValue {
  return useContext(AccessPermissionsContext);
}

// True when the user holds any system-settings Permission. Drives whether
// the Systemeinstellungen entry point and its routes are shown at all - the
// replacement for roles.ts's retired useHasSystemAccess.
export function useHasSystemPermission(): boolean {
  const { hasPermission } = useAccessPermissions();
  return SYSTEM_PERMISSIONS.some(hasPermission);
}

// The non-hook counterpart for React Router loaders, which run before the
// component tree mounts and so cannot reach AccessPermissionsProvider's
// context. Mirrors bootstrapAuth's pattern of an imperative call against the
// same ApolloClient singleton the provider's useQuery also reads through.
// network-only: a loader's answer must be current, not a stale cache hit
// from before the last grant change.
export async function fetchPermissionCodes(): Promise<string[]> {
  const { data } = await apolloClient.query({
    query: MyPermissionsDocument,
    fetchPolicy: "network-only",
  });
  return data?.myPermissions.permissionCodes ?? [];
}

export function hasAnySystemPermissionCode(codes: string[]): boolean {
  return SYSTEM_PERMISSIONS.some((code) => codes.includes(code));
}
